import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/shared/config/env';
import { getApiBridge } from './bridge';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Adjunta Authorization: Bearer <access>. Solo las rutas privadas lo piden (las públicas
     *  quedan como "peticiones simples": sin preflight CORS y cacheables por el service worker). */
    withAuth?: boolean;
    /** Interno: ya se reintentó tras renovar la sesión. */
    _retried?: boolean;
  }
}

export const httpClient: AxiosInstance = axios.create({
  baseURL: `${env.apiBaseUrl}${env.apiVersion}`,
  timeout: env.timeoutMs,
  headers: { Accept: 'application/json' },
  // Cookie de refresh (modo cookie, ver docs/SEGURIDAD.md). Sin efecto si el backend no la usa.
  withCredentials: false,
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.withAuth) {
    const token = getApiBridge().getAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

httpClient.interceptors.response.use(
  (response) => {
    getApiBridge().onReachability?.(true);
    return response;
  },
  async (error: AxiosError) => {
    const bridge = getApiBridge();
    const status = error.response?.status ?? 0;
    const config = error.config;

    if (!axios.isCancel(error)) bridge.onReachability?.(status !== 0);

    if (status === 401 && config?.withAuth && !config._retried) {
      refreshing ??= bridge.refreshSession().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (token) {
        config._retried = true;
        config.headers.set('Authorization', `Bearer ${token}`);
        return httpClient.request(config);
      }
      bridge.onSessionLost();
    } else if (status >= 500) {
      bridge.onServerError?.(status);
    }
    return Promise.reject(error);
  },
);
